import React, { useCallback, useEffect, useRef, useState } from 'react';
import X from '@uber/base-icons/x';
import { type Theme, useStyletron } from 'baseui';
import { CSS_VARIABLE_STAGE_VISIBLE_SCALE } from '../../../constants/stage-css-variables';
import { CANVAS, HORIZONTAL_ANCHOR, VERTICAL_ANCHOR_TRANSLATE } from '../../../constants/template-builder';
import type { HorizontalAnchorT, VerticalAnchorT } from '../../../types/annotations.type';
import { getHorizontalAnchor, getScaledNodeOffset, getVerticalAnchor } from '../../../utils/node-position';
type NodeNameInputPropsT = {
  x: number;
  y: number;
  canvasDimensions: [number, number];
  onConfirm: (label: string) => void;
  onCancel: () => void;
};
const getContainerStyles = ({
  theme,
  x,
  y,
  horizontalAnchor,
  verticalAnchor
}: {
  theme: Theme;
  x: number;
  y: number;
  horizontalAnchor: HorizontalAnchorT;
  verticalAnchor: VerticalAnchorT;
}) => ({
  position: ('absolute' as const),
  ...(horizontalAnchor === HORIZONTAL_ANCHOR.RIGHT ? {
    right: `calc(100% - ${x}px + ${getScaledNodeOffset()})`
  } : {
    left: `calc(${x}px + ${getScaledNodeOffset()})`
  }),
  top: `${y}px`,
  transform: `${VERTICAL_ANCHOR_TRANSLATE[verticalAnchor]} scale(var(${CSS_VARIABLE_STAGE_VISIBLE_SCALE}))`,
  transformOrigin: `${horizontalAnchor} ${verticalAnchor}`,
  zIndex: 10,
  display: ('flex' as const),
  alignItems: ('center' as const),
  gap: theme.sizing.scale500,
  width: CANVAS.INPUT_WIDTH,
  boxSizing: ('border-box' as const),
  overflow: ('hidden' as const),
  backgroundColor: theme.colors.backgroundPrimary,
  borderRadius: theme.borders.radius200,
  border: `1px solid ${theme.colors.borderTransparent}`,
  boxShadow: theme.lighting.shadow400,
  paddingTop: theme.sizing.scale400,
  paddingBottom: theme.sizing.scale400,
  paddingLeft: theme.sizing.scale500,
  paddingRight: theme.sizing.scale500
});
const getInputStyles = (theme: Theme) => ({
  border: 'none',
  outline: 'none',
  ...theme.typography.LabelSmall,
  flex: 1,
  minWidth: '0',
  backgroundColor: 'transparent'
});
const getCloseButtonStyles = (theme: Theme) => ({
  border: 'none',
  backgroundColor: 'transparent',
  cursor: ('pointer' as const),
  padding: '0',
  display: ('flex' as const),
  alignItems: ('center' as const),
  flexShrink: 0,
  color: theme.colors.contentTertiary,
  ':hover': {
    color: theme.colors.contentPrimary
  }
});
export function NodeNameInput({
  x,
  y,
  canvasDimensions,
  onConfirm,
  onCancel
}: NodeNameInputPropsT) {
  const [css, theme] = useStyletron();
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [canvasWidth, canvasHeight] = canvasDimensions;
  const horizontalAnchor = getHorizontalAnchor(x, canvasWidth);
  const verticalAnchor = getVerticalAnchor(y, canvasHeight);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === 'Enter' && value) {
      onConfirm(value);
    } else if (e.key === 'Escape') {
      onCancel();
    }
  }, [value, onConfirm, onCancel]);
  const handleBlur = useCallback(() => {
    if (value) {
      onConfirm(value);
    } else {
      onCancel();
    }
  }, [value, onConfirm, onCancel]);
  return <div className={css(getContainerStyles({
    theme,
    x,
    y,
    horizontalAnchor,
    verticalAnchor
  }))} onPointerDown={e => e.stopPropagation()} onPointerUp={e => e.stopPropagation()}>
      <input ref={inputRef} value={value} onChange={e => setValue(e.target.value)} onKeyDown={handleKeyDown} onBlur={handleBlur} placeholder="Name" className={css(getInputStyles(theme))} />
      <button onMouseDown={e => e.preventDefault()} onClick={onCancel} className={css(getCloseButtonStyles(theme))} type="button">
        <X size={16} />
      </button>
    </div>;
}